import { useState } from 'react';
import type { Chat } from '@/data/types';
import { addReminder, changeReminder, closeReminder, monthsOn, shortDate, todayIso, type Reminder } from '@/lib/reminders';

// Add, change, tick off or remove a chat's reminder (Gary, 3 Nov 2026). On the day the chat comes back to its owner's
// Reply tab with a phone alert, as Leo's reminders always have.

const QUICK: { label: string; months: number }[] = [
  { label: '1 month', months: 1 },
  { label: '3 months', months: 3 },
  { label: '6 months', months: 6 },
];

export function ReminderSheet({ chat, reminder, userId, onDone }: {
  chat: Pick<Chat, 'id' | 'customerId' | 'leadId' | 'name'>;
  reminder: Reminder | null;
  userId: string;
  onDone: (message: string) => void;
}) {
  const [date, setDate] = useState(reminder?.date ?? monthsOn(3));
  const [note, setNote] = useState(reminder?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= today;

  const run = (job: () => Promise<void>, message: string) => {
    setBusy(true); setError(null);
    job().then(() => onDone(message))
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : 'That did not save. Try again.'); setBusy(false); });
  };
  const save = () => run(
    () => (reminder
      ? changeReminder(reminder.id, { date, note })
      : addReminder({ chat, date, note, type: 'follow_up', userId })),
    `Reminder set for ${shortDate(date)}`,
  );

  return (
    <div className="chats-pad">
      <h2 className="chats-sheet-title">{reminder ? 'Reminder' : 'Add a reminder'}</h2>
      <p className="chats-sheet-hint">
        {reminder?.setBy === null ? 'Set by Leo. ' : reminder?.setBy ? `Set by ${reminder.setBy}. ` : ''}
        On the day, this chat comes back to you with an alert.
      </p>
      {error && <p className="chats-rec-error" role="alert">{error}</p>}
      <div className="chats-chips" role="group" aria-label="Quick dates">
        {QUICK.map((q) => {
          const d = monthsOn(q.months);
          return <button key={q.label} type="button" className={`chats-chip${date === d ? ' active' : ''}`} onClick={() => setDate(d)}>{q.label}</button>;
        })}
      </div>
      <input className="chats-input chats-input--stack" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Reminder date" />
      <textarea className="chats-input chats-input--stack" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="What to follow up, e.g. book their next clean" aria-label="What to follow up" />
      <button type="button" className="chats-btn" disabled={busy || !valid} onClick={save}>{reminder ? 'Save' : 'Set reminder'}</button>
      {reminder && (
        <div className="chats-pair chats-reminder-close">
          <button type="button" className="chats-btn chats-btn--ghost" disabled={busy} onClick={() => run(() => closeReminder(reminder.id, 'cancelled'), 'Reminder removed')}>Remove</button>
          <button type="button" className="chats-btn chats-btn--ghost" disabled={busy} onClick={() => run(() => closeReminder(reminder.id, 'done'), 'Reminder done')}>Done</button>
        </div>
      )}
    </div>
  );
}
